import { NavLink, Route, Routes, Navigate, Link } from "react-router-dom";
import { useT, useLang } from "./i18n/useT";
import { useStore } from "./store/store";
import { Dashboard } from "./pages/Dashboard";
import { Campaigns } from "./pages/Campaigns";
import { CampaignDetail } from "./pages/CampaignDetail";
import { Flows } from "./pages/Flows";
import { Contacts } from "./pages/Contacts";
import { ConsentLedger } from "./pages/ConsentLedger";
import { Reports } from "./pages/Reports";
import { ManualSend } from "./pages/ManualSend";
import { AgentConsole } from "./pages/AgentConsole";
import { Supervisor } from "./pages/Supervisor";
import { Settings } from "./pages/Settings";
import { Marketing } from "./pages/Marketing";
import { BlogIndex, BlogPost } from "./pages/Blog";
import { PulseGlyph } from "./components/Logo";

export function App() {
  return (
    <Routes>
      <Route path="/" element={<Marketing />} />
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/app" element={<Navigate to="/app/dashboard" replace />} />
      <Route path="/app/*" element={<AdminShell />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function AdminShell() {
  const t = useT();
  const lang = useLang();
  const setLanguage = useStore((s) => s.setLanguage);

  return (
    <div className="min-h-full grid grid-cols-[260px_1fr]">
      <aside className="border-r border-slate-200 bg-white px-4 py-5 flex flex-col">
        <Link to="/" className="flex items-center gap-2 px-2 mb-6 group">
          <div className="relative grid h-9 w-9 place-items-center overflow-hidden rounded-lg bg-ink-900">
            <PulseGlyph />
            <span aria-hidden className="absolute -right-2 -top-2 h-4 w-4 rounded-full bg-accent-400" />
            <span aria-hidden className="absolute -bottom-2 -left-2 h-4 w-4 rounded-full bg-brand-400" />
          </div>
          <div>
            <div className="font-semibold leading-tight group-hover:text-brand-700">{t.appName}</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 leading-tight">{t.tagline}</div>
          </div>
        </Link>
        <nav className="flex flex-col gap-1">
          <NavItem to="/app/dashboard" label={t.nav.dashboard} />
          <NavItem to="/app/campaigns" label={t.nav.campaigns} />
          <NavItem to="/app/flows" label={t.nav.flows} />
          <NavItem to="/app/contacts" label={t.nav.contacts} />
          <NavItem to="/app/consent" label={t.nav.consent} />
          <NavItem to="/app/reports" label={t.nav.reports} />
          <NavItem to="/app/send" label={t.nav.send} />
          <NavItem to="/app/agent" label="Agent console" />
          <NavItem to="/app/supervisor" label="Supervisor" />
          <NavItem to="/app/settings" label={t.nav.settings} />
        </nav>
        <div className="mt-auto pt-4 border-t border-slate-200 space-y-3">
          <Link to="/" className="block text-xs text-slate-500 hover:text-brand-700">← {lang === "es" ? "Volver al sitio" : "Back to site"}</Link>
          <div>
            <label className="label">{t.common.language}</label>
            <div className="flex gap-1">
              <button
                onClick={() => setLanguage("en")}
                className={`btn ${lang === "en" ? "btn-primary" : "btn-secondary"} flex-1`}
              >EN</button>
              <button
                onClick={() => setLanguage("es")}
                className={`btn ${lang === "es" ? "btn-primary" : "btn-secondary"} flex-1`}
              >ES</button>
            </div>
          </div>
        </div>
      </aside>
      <main className="p-8 overflow-auto">
        <Routes>
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="campaigns" element={<Campaigns />} />
          <Route path="campaigns/:id" element={<CampaignDetail />} />
          <Route path="flows" element={<Flows />} />
          <Route path="contacts" element={<Contacts />} />
          <Route path="consent" element={<ConsentLedger />} />
          <Route path="reports" element={<Reports />} />
          <Route path="send" element={<ManualSend />} />
          <Route path="agent" element={<AgentConsole />} />
          <Route path="supervisor" element={<Supervisor />} />
          <Route path="settings" element={<Settings />} />
          <Route path="*" element={<Navigate to="/app/dashboard" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function NavItem({ to, label }: { to: string; label: string }) {
  return (
    <NavLink to={to} end className={({ isActive }) => `nav-link ${isActive ? "nav-link-active" : ""}`}>
      {label}
    </NavLink>
  );
}
